/**
 * Per-trade value derivation. Phase 1, module 2 of 4.
 *
 * PORTED FROM `tradecoach-saas/app/index.html` — semantics, not code (D3).
 *
 * ⚠ ARCHITECTURAL SPLIT, deliberate and worth understanding before editing.
 *
 * The old `countsForMetrics(t)` is:
 *
 *     tradeHasCore(t) && (!accountExcluded(t.accountId) || selectionIsExcludedOnly())
 *
 * That last term reads `getActiveAccountIds()` — the **account dropdown's
 * current selection**. It is UI state, so the function as written cannot live
 * in a pure layer.
 *
 * The split here keeps the RULE in domain and moves the STATE LOOKUP out:
 * `countsForMetrics` takes the two facts as arguments, and `selectors/`
 * computes them from live state. Behaviour is identical; the dependency
 * direction is not.
 */
import type { PnlUnit } from './types';

// ---------------------------------------------------------------------------
// Contract resolution
// ---------------------------------------------------------------------------
// Getting this wrong does not produce a wrong number — it produces `null`,
// which drops the trade out of tick-denominated aggregates entirely. Silent
// under-counting rather than a visible error, so the fallback chain matters.

export interface ContractSpec {
  symbol: string;
  tickSize: number | null;
  dollarsPerTick: number | null;
  dollarsPerPoint: number | null;
}

/** Month codes, in exchange order. */
const MONTH_CODES = 'FGHJKMNQUVXZ';

const EXCHANGE_SUFFIX =
  /-(CME|CBOT|NYMEX|COMEX|GLOBEX|ICE|ICEUS|NYBOT|EUREX|CFE|DME|SGX)$/;

/**
 * Strip broker decorations before matching: a leading bracket tag like `[Sim]`
 * (Sierra Chart simulated accounts) and a trailing exchange suffix.
 *
 *   "[Sim]ESU26-CME" → "ESU26"
 *
 * Shared by root resolution and full-symbol capture so a decorated symbol
 * cannot reach the database unpriceable.
 *
 * ⚠ THE REASON CHANGED AND THE RULE DID NOT. This used to be about the
 * `trades_contract_fkey` constraint, where one uncatalogued root aborted a whole
 * import batch; `20260906000006` dropped that constraint so traders can add
 * their own instruments. A decorated symbol now fails silently instead of
 * loudly — it resolves to nothing, prices at $0, and imports anyway unless
 * `assembleImported` catches it. Stripping is what stops it reaching either.
 */
export function stripSymbolDecorations(raw: string | null | undefined): string {
  return String(raw ?? '')
    .toUpperCase()
    .trim()
    .replace(/^\[[^\]]*\]\s*/, '')
    .replace(EXCHANGE_SUFFIX, '')
    .trim();
}

/** CQG/broker root aliases — their symbol for a product differs from the exchange's. */
export const CQG_ROOT_ALIASES: Readonly<Record<string, string>> = {
  EP: 'ES',
  ENQ: 'NQ',
};

/** `"F.US.MNQM26"` → `"MNQ"`. Drops the routing prefix and the month+year suffix. */
export function cqgSymbolRoot(sym: string | null | undefined): string {
  let s = String(sym ?? '').toUpperCase().trim();
  s = s.replace(/^F\.[A-Z0-9]+\./, '');
  const m = s.match(new RegExp(`^([A-Z0-9]+?)[${MONTH_CODES}]\\d{2}$`));
  const root = m ? (m[1] as string) : s;
  return CQG_ROOT_ALIASES[root] ?? root;
}

/**
 * Resolve a symbol to a catalogue entry. Four attempts, in order — exact,
 * prefix, CQG root, root prefix — because broker exports disagree about how
 * much of the contract month to include.
 */
export function findContract(
  sym: string | null | undefined,
  catalogue: readonly ContractSpec[],
): ContractSpec | null {
  if (!sym) return null;
  const upper = stripSymbolDecorations(sym);

  const exact = catalogue.find((x) => x.symbol === upper);
  if (exact) return exact;

  const prefix = longestPrefixContract(upper, catalogue);
  if (prefix) return prefix;

  const root = cqgSymbolRoot(upper);
  if (root && root !== upper) {
    const byRoot =
      catalogue.find((x) => x.symbol === root) ??
      longestPrefixContract(root, catalogue);
    if (byRoot) return byRoot;
  }
  return null;
}

/**
 * The LONGEST catalogue root that prefixes `upper`, not the first in order.
 *
 * ⚠ This was a 5x P&L error in the shipping client. `SI` (Silver, $5,000/pt) is
 * a strict prefix of `SIL` (Micro Silver, $1,000/pt), so a first-match scan
 * resolved `SILU26` to Silver — wrong $/point on import, and wrong
 * points/ticks/R everywhere downstream.
 *
 * Order-independent by design. Reordering the catalogue could not have fixed it:
 * the client loads `contracts` with `.order('symbol')`, and alphabetical order
 * always places a prefix ahead of the string it prefixes. This also protects
 * user-added contracts, where no curated ordering exists at all.
 *
 * Cannot change WHETHER a symbol resolves — it picks differently within the same
 * candidate set — so nothing newly falls through to the CQG fallback.
 */
function longestPrefixContract(
  upper: string,
  catalogue: readonly ContractSpec[],
): ContractSpec | null {
  let best: ContractSpec | null = null;
  for (const x of catalogue) {
    if (!x?.symbol || !upper.startsWith(x.symbol)) continue;
    if (!best || x.symbol.length > best.symbol.length) best = x;
  }
  return best;
}

export function dollarsPerPointFor(
  contract: string | null,
  catalogue: readonly ContractSpec[],
): number {
  if (!contract) return 0;
  return findContract(contract, catalogue)?.dollarsPerPoint ?? 0;
}

// ---------------------------------------------------------------------------
// Value in a display unit
// ---------------------------------------------------------------------------

/** The fields `tradeValue` reads — deliberately narrower than a full Trade. */
export interface ValuedTrade {
  contract: string | null;
  points: number | null;
  netPnl: number | null;
  rrRealized: number | null;
}

/**
 * A trade's realised result expressed in `unit`.
 *
 * `null` is a meaningful answer, not a failure, and the two cases differ:
 *   - `ticks` — the contract is not in the catalogue, so no tick size exists
 *   - `r`     — the trade had no stop, so there is no realised R
 *
 * Both must stay `null` rather than becoming 0. A zero would be averaged in and
 * silently drag every R and tick aggregate toward zero; a null is skipped.
 */
export function tradeValue(
  t: ValuedTrade | null | undefined,
  unit: PnlUnit,
  catalogue: readonly ContractSpec[],
): number | null {
  if (!t) return null;
  switch (unit) {
    case 'points':
      return Number(t.points) || 0;
    case 'ticks': {
      const spec = findContract(t.contract, catalogue);
      const ts = spec && Number(spec.tickSize) > 0 ? Number(spec.tickSize) : null;
      return ts ? (Number(t.points) || 0) / ts : null;
    }
    case 'r': {
      const r = t.rrRealized;
      return r == null || !isFinite(Number(r)) ? null : Number(r);
    }
    case 'usd':
    default:
      return Number(t.netPnl) || 0;
  }
}

/** Extra fields the excursion form needs on top of `ValuedTrade`. */
export interface ExcursionValuedTrade extends ValuedTrade {
  qty: number | null;
  riskPoints: number | null;
}

/**
 * An excursion magnitude (|MAE| / |MFE|, in points) expressed in `unit`,
 * mirroring `tradeValue`'s per-unit treatment so the two follow the same toggle.
 *
 * Note `usd` differs from `tradeValue`: excursion is a per-contract points
 * magnitude, so it must be scaled by $/point AND quantity. `tradeValue`'s
 * `netPnl` is already qty-adjusted. Conflating the two would misprice every
 * give-back and premature-exit figure by the position size.
 */
export function excursionValue(
  t: ExcursionValuedTrade | null | undefined,
  unit: PnlUnit,
  points: number | null,
  catalogue: readonly ContractSpec[],
): number | null {
  if (!t || points == null) return null;
  const p = Math.abs(Number(points) || 0);
  switch (unit) {
    case 'points':
      return p;
    case 'ticks': {
      const spec = findContract(t.contract, catalogue);
      const ts = spec && Number(spec.tickSize) > 0 ? Number(spec.tickSize) : null;
      return ts ? p / ts : null;
    }
    case 'r': {
      const rp = Number(t.riskPoints);
      return rp > 0 ? p / rp : null;
    }
    case 'usd':
    default:
      return p * dollarsPerPointFor(t.contract, catalogue) * (Number(t.qty) || 1);
  }
}

// ---------------------------------------------------------------------------
// The metrics seam
// ---------------------------------------------------------------------------

/** A saved trade with a core record. The base check for per-account views. */
export function tradeHasCore(t: unknown): boolean {
  return !!t;
}

export interface MetricsContext {
  /** The trade's account carries the "exclude from metrics" flag (SIM / replay). */
  accountExcluded: boolean;
  /**
   * The user's CURRENT account selection contains only excluded accounts.
   *
   * Computed by `selectors/` from the live filter — this is the UI-state term
   * that keeps `countsForMetrics` out of a pure layer if inlined.
   */
  selectionIsExcludedOnly: boolean;
}

/**
 * Whether a trade counts toward QUANTITATIVE metrics — P&L, win rate,
 * expectancy, equity, drawdown, balance, RoR, calendar.
 *
 * ⚠ Quantitative value exists the moment a trade is saved; it does NOT require
 * annotation. The old client once gated this on `annotated !== false`, which
 * left imported trades showing blank KPIs and wrong balances. **Do not
 * reintroduce an annotation check here** — qualitative analytics
 * (pattern / exit-model / confluence / grade) gate themselves on their own tags
 * and deliberately do not route through this seam.
 *
 * The excluded-account rule is conditional on purpose: selecting an excluded
 * account and seeing every number blank reads as broken, so when the selection
 * is excluded-only the exclusion lifts. Under "All accounts", or any mixed
 * selection, nothing changes.
 */
export function countsForMetrics(t: unknown, ctx: MetricsContext): boolean {
  if (!tradeHasCore(t)) return false;
  if (!ctx.accountExcluded) return true;
  return ctx.selectionIsExcludedOnly;
}
